import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Sprite } from '@pixi/react';
import { gsap } from 'gsap';
import { observer } from 'mobx-react-lite';
import { getGameImages } from '../../../core/helpers/getGameImages';
import { textureChangeFrame } from '../../../core/helpers/textureChangeFrame';
import { gameStore } from '../../../store/gameStore';
import { gameDrawer } from '../../../services/gameDrawer';
import { bettingPartStore } from '../../../store/bettingPartStore';
import { gambleStore } from '../../../store/gambleStore';
import IconBackGround from '../../common/iconBackGround';
import { isMobile } from '../../../core/helpers/isMobile';
import { GAME_SPEED_TYPES } from '../../../core/constants/gameConstants';
import { gameGlobalStates } from '../../../core/constants/reelConstants';
import { winPopupStore } from '../../../store/winPopupStore';
import { userInfoStore } from '../../../store/userInfoStore';

const SpinButton = observer(({ x = 0, y = 0 }) => {
  const [isSpinAnimationEnd, setIsSpinAnimationEnd] = useState(false);
  const { isSpinStart, winEnded, isSpinButtonHold } = gameStore;
  const { isReplay } = userInfoStore;
  const { isShowGamblePopup } = gambleStore;
  const { isAutoBetStart, gameSpeedType } = bettingPartStore;
  const { isOpenWinPopup } = winPopupStore;
  const {
    spinIconBgSelected,
    spinIconBgDefault,
    spinIconBgPressed,
    spinDefault,
    spinPressed,
    spinStop,
  } = useMemo(() => getGameImages(), []);
  const spinButtonContent = useRef(null);

  const autoSpinIntervalRef = useRef(null);
  const isMouseHeld = useRef(false);
  const isKeyHeld = useRef(false);
  const previousSpeedType = useRef(null);

  const handleSpin = useCallback(() => {
    if (gameStore.gameGlobalState !== gameGlobalStates.waiting) {
      gameDrawer.updateReelsDirection(1);
    }
    gameDrawer.spinAction();
  }, []);

  const handlePointerUp = useCallback(() => {
    if (autoSpinIntervalRef.current) {
      isMouseHeld.current = false;
      gameStore.setIsSpinButtonHold(false);
      bettingPartStore.setGameSpeedType(previousSpeedType.current);
      clearInterval(autoSpinIntervalRef.current);
      autoSpinIntervalRef.current = null;
    }
  }, []);

  const handlePointerDown = useCallback(
    (e) => {
      if (e) {
        if (typeof e.buttons !== 'undefined' && e.buttons !== 1) return;
        if (typeof e.button !== 'undefined' && e.button !== 0) return;
      }
      if (!winEnded || isReplay) return;
      console.log(isKeyHeld.current, isMouseHeld.current);
      if (isKeyHeld.current && isMouseHeld.current) return;
      if (!isKeyHeld.current) isMouseHeld.current = true;
      if (
        spinButtonContent.current &&
        !isSpinStart &&
        !isAutoBetStart &&
        !isOpenWinPopup &&
        !isSpinButtonHold
      ) {
        gsap.to(spinButtonContent.current, {
          rotation: Math.PI * 0.5,
          duration: 0.2,
          delay: 0.1,
          ease: 'none',
          onComplete: () => {
            setTimeout(() => {
              setIsSpinAnimationEnd(true);
            }, 200);
            if (isSpinStart) {
              textureChangeFrame(spinButtonContent.current, spinStop);
            } else {
              textureChangeFrame(spinButtonContent.current, spinPressed);
            }
            spinButtonContent.current.rotation = 0;
          },
        });
        previousSpeedType.current = gameSpeedType;
        bettingPartStore.setGameSpeedType(GAME_SPEED_TYPES.turbo);
        gameStore.setIsSpinButtonHold(true);
        autoSpinIntervalRef.current = setInterval(() => {
          handleSpin();
        }, 200);
      }
    },
    [
      isReplay,
      gameSpeedType,
      isOpenWinPopup,
      winEnded,
      isSpinStart,
      isAutoBetStart,
      spinPressed,
      spinStop,
      handleSpin,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.code === 'Space' || e.code === 'Enter') && !isKeyHeld.current && !isReplay) {
        isKeyHeld.current = true;
        e.preventDefault();
        handlePointerDown();
        handleSpin();
      }
    },
    [isReplay, handlePointerDown, handleSpin]
  );

  const handleKeyUp = useCallback(
    (e) => {
      if ((e.code === 'Space' || e.code === 'Enter') && !isReplay) {
        isKeyHeld.current = false;
        handlePointerUp();
      }
    },
    [isReplay, handlePointerUp]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  const additionalBackGroundImages = useMemo(
    () => ({
      iconBgDefault: spinIconBgDefault,
      iconBgDisabled: spinIconBgDefault,
      iconBgHovered: spinIconBgDefault,
      iconBgPressed: spinIconBgPressed,
      iconBgSelected: spinIconBgSelected,
    }),
    [spinIconBgDefault, spinIconBgPressed, spinIconBgSelected]
  );

  useEffect(() => {
    if (!isAutoBetStart && !isSpinStart && spinButtonContent.current && !isSpinAnimationEnd) {
      textureChangeFrame(spinButtonContent.current, spinDefault);
      setIsSpinAnimationEnd(false);
    }
    if ((isSpinStart && isSpinAnimationEnd) || isAutoBetStart) {
      textureChangeFrame(spinButtonContent.current, spinStop);
      setIsSpinAnimationEnd(false);
    }
  }, [isSpinStart, isAutoBetStart, spinDefault, isSpinAnimationEnd, spinStop]);

  useEffect(() => {
    if (isAutoBetStart) textureChangeFrame(spinButtonContent.current, spinStop);
  }, [isAutoBetStart, spinStop]);

  if (isShowGamblePopup) return null;

  return (
    <IconBackGround
      x={x}
      y={y}
      isDisabled={isReplay}
      pointerOut={handlePointerUp}
      additionalBackGroundImages={additionalBackGroundImages}
      handleSelect={handleSpin}
      pointerDown={handlePointerDown}
      pointerUp={handlePointerUp}
    >
      <Sprite
        ref={spinButtonContent}
        texture={spinDefault}
        scale={isMobile() ? 0.77 : 0.5}
        anchor={0.5}
      />
    </IconBackGround>
  );
});

export default SpinButton;
